import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { exchangeAdvancedRecovery } from './advancedAssessmentApi';

const Shell = styled.section`
  width: min(680px, calc(100% - 32px));
  margin: clamp(48px, 9vw, 110px) auto;
  padding: clamp(28px, 6vw, 56px);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  background: white;
  box-shadow: var(--shadow-sm);

  h1 { margin: 0 0 16px; color: var(--color-accent-dark); font-size: clamp(2rem, 5vw, 3rem); }
  p { color: var(--text-secondary); line-height: 1.65; }
  button, a {
    min-height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
    padding: 12px 20px;
    border: 1px solid var(--color-accent-primary);
    border-radius: 8px;
    background: var(--color-accent-primary-fill);
    color: white;
    font: inherit;
    font-weight: 800;
    text-decoration: none;
    cursor: pointer;
  }
  button:disabled { cursor: wait; opacity: 0.65; }
  button:focus-visible, a:focus-visible { outline: 3px solid rgba(11, 152, 206, 0.4); outline-offset: 3px; }
`;

const AssessmentRecovery: React.FC = () => {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Remove the bearer credential before any later navigation, support action, or
    // client-side page-view code can observe it. Email scanners only GET this page;
    // recovery happens solely after the deliberate POST below.
    window.history.replaceState({}, '', '/assessment/recovery');
    heading.current?.focus();
  }, []);

  const confirmRecovery = async () => {
    if (!token) {
      setError('This recovery link is invalid or expired. Request a new private link.');
      return;
    }
    setIsBusy(true);
    setError('');
    try {
      const result = await exchangeAdvancedRecovery(token);
      sessionStorage.setItem(`davinci-assessment-${result.sessionId}`, result.accessToken);
      window.location.assign(`/assessment?advanced_session=${result.sessionId}`);
    } catch (exchangeError) {
      setError(exchangeError instanceof Error
        ? exchangeError.message
        : 'This recovery link is invalid or expired. Request a new private link.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Shell data-clarity-mask="True" aria-labelledby="assessment-recovery-heading">
      <h1 id="assessment-recovery-heading" ref={heading} tabIndex={-1}>Recover your private assessment</h1>
      <p>This link contains no assessment answers. Confirm below to rotate the private browser key and continue on this device.</p>
      <p>For your security, opening this page does not restore access. The link can be used once and expires after 30 minutes.</p>
      <button type="button" onClick={confirmRecovery} disabled={isBusy || !token}>
        {isBusy ? 'Recovering…' : 'Confirm and recover access'}
      </button>
      {!token && <p role="alert">This recovery link is invalid or expired. Request a new private link.</p>}
      {error && <p role="alert">{error}</p>}
      <p><a href="/contact">Contact Davinci support</a></p>
    </Shell>
  );
};

export default AssessmentRecovery;
